import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { FormProvider, useNotificationContext } from 'core/states';
import { getDateForInput } from 'core/utils';
import { PricingProfilePricingType } from 'pricing/enums';
import { usePricingProfileActions } from 'pricing/hooks';
import { mapUpdatePricingProfiles } from 'pricing/mappers';
import { IPricingProfile, IPricingProfileForm, PricingProfileAccessType } from 'pricing/types';

import { pricingProfileUpdateSchema } from './schema';
import { UpdatePricingProfilesFormFields } from './UpdatePricingProfilesFormFields';

interface IProps {
  pricingProfiles: IPricingProfile[];
  type: PricingProfilePricingType;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const UpdatePricingProfiles = ({
  pricingProfiles,
  type,
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertPricingProfiles } = usePricingProfileActions({ type });

  const isSingleProfile = pricingProfiles.length === 1;
  const profile = isSingleProfile ? pricingProfiles[0] : null;

  const defaultValues = useMemo(() => {
    if (isOpen && profile) {
      return {
        name: profile.name,
        agreementId: profile.agreementId,
        startDate: getDateForInput(profile.startDate),
        endDate: getDateForInput(profile.endDate),
        shareAccess: profile.accessType === PricingProfileAccessType.COMPANY,
      };
    }
  }, [isOpen, profile]);

  const onSubmit =
    (reset: UseFormReset<IPricingProfileForm>) => async (data: IPricingProfileForm) => {
      const { success } = await upsertPricingProfiles(
        mapUpdatePricingProfiles(pricingProfiles, data)
      );

      if (!success) {
        return;
      }

      setTimeout(() => {
        onSubmitSuccess?.();
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset: UseFormReset<IPricingProfileForm>) => {
    clearNotification();
    onClose();
    reset(defaultValues);
  };

  return (
    <FormProvider
      schema={pricingProfileUpdateSchema(isSingleProfile)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpdatePricingProfilesFormFields
        pricingProfiles={pricingProfiles}
        type={type}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
